import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import {
  CreateEventRequest,
  EventRecord,
  UpdateEventRequest,
} from "@/shared/domain/events";
import {
  eventQueryKeys,
  type EventsRequest,
} from "@/shared/domain/events/hooks";
import { type Datasource } from "@/shared/state/visualization";
import { useDatasources } from "@/shared/state/visualization/hooks";

import { useEphemeralWorkspaceState, useWorkspaceSessions } from "./hooks";

interface SelectOptions {
  datasetIds?: string[];
  fileIds?: string[];
  topicIds?: string[];
  messagePathIds?: string[];
}

type UseWorkspaceEventsOptions = Partial<{
  select: SelectOptions;
}>;

const unique = (ids: string[]): string[] => [...new Set(ids)];

/**
 * The subset of {@link EventsRequest} a workspace produces. `EventsRequest`
 * leaves every field optional because other callers query by topic or message
 * path; a workspace always states both of its own associations, even when both
 * are empty.
 */
type WorkspaceEventsRequest = EventsRequest & {
  datasetIds: string[];
  fileIds: string[];
};

/**
 * The workspace's event-query identity: the datasets and files that define it.
 * Dataset and directly-named file datasources are stated at datasource level (a
 * dataset with thousands of files is one association, not thousands).
 *
 * A session datasource is a reference with no files of its own, so its events
 * come from `sessionFileIds` — the files the fetcher resolved for every loaded
 * session. Those land after the workspace's files resolve, so unlike the
 * datasource-only ids this part of the request (and its query key) changes once
 * per load, and the events query refetches when session files arrive.
 *
 * Exported for tests.
 */
export function deriveWorkspaceEventsRequest(
  datasources: Datasource[],
  sessionFileIds: string[],
): WorkspaceEventsRequest {
  const datasetIds = unique(
    datasources
      .filter((datasource) => datasource.type === "dataset")
      .map((datasource) => datasource.value.datasetId),
  );

  const directFileIds = datasources
    .filter((datasource) => datasource.type === "file")
    .map((datasource) => datasource.value.fileId);

  return {
    datasetIds,
    fileIds: unique([...directFileIds, ...sessionFileIds]),
  };
}

/**
 * Memoized {@link deriveWorkspaceEventsRequest} over the open workspace. Shared
 * between the query and the mutation hooks so their optimistic cache writes land
 * on the same cache slot.
 */
function useWorkspaceEventsRequest(): WorkspaceEventsRequest {
  const datasources = useDatasources();
  const sessions = useWorkspaceSessions();

  const sessionFileIds = React.useMemo(
    () =>
      sessions.flatMap((session) => session.files.map((file) => file.file_id)),
    [sessions],
  );

  return React.useMemo(
    () => deriveWorkspaceEventsRequest(datasources, sessionFileIds),
    [datasources, sessionFileIds],
  );
}

export function useWorkspaceEvents(options?: UseWorkspaceEventsOptions) {
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  const { isDataLoading } = useEphemeralWorkspaceState();
  const workspaceRequest = useWorkspaceEventsRequest();

  return useQuery({
    queryKey: eventQueryKeys.forWorkspace(orgId, workspaceRequest),
    // One viz-specific request describing the workspace's data sources. The server
    // expands datasets/files transitively, infers each loose file's parent dataset to
    // include its dataset-wide events, and dedupes — so there's nothing to merge here.
    queryFn: ({ signal }) => {
      return events.getWorkspaceEvents(
        {
          datasetIds: workspaceRequest.datasetIds,
          fileIds: workspaceRequest.fileIds,
        },
        // The org scopes which custom-field schema the events come back with.
        { abortSignal: signal, resourceOwnerId: orgId },
      );
    },
    select: (data) => {
      if (options?.select) {
        return selectEvents(data, options.select);
      }

      return data;
    },
    enabled: !isDataLoading,
    staleTime: 300_000, // 5 minutes
  });
}

export function useWorkspaceEvent(eventId: string) {
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  return useQuery({
    queryKey: eventQueryKeys.detail(orgId, eventId),
    queryFn: ({ signal }) => {
      return events.getEventById(eventId, {
        abortSignal: signal,
      });
    },
    staleTime: 300_000, // 5 minutes
  });
}

export function useCreateWorkspaceEvent() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();

  const orgId = useCurrentOrgId();
  const workspaceRequest = useWorkspaceEventsRequest();

  return useMutation({
    mutationFn: function createWorkspaceEvent(request: CreateEventRequest) {
      return events.createEvent(orgId, request);
    },
    onSuccess: (event: EventRecord) => {
      queryClient.setQueryData(
        eventQueryKeys.forWorkspace(event.org_id, workspaceRequest),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }
          return [...oldData, event];
        },
      );
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(event.org_id),
      });
      // Other workspace views may include this event via its
      // associations — mark them stale for next mount. `refetchType:
      // "none"` avoids re-fetching the currently-active workspace,
      // which we just updated via `setQueryData` above.
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(event.org_id),
        refetchType: "none",
      });
    },
  });
}

export function useDeleteWorkspaceEvent() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  const workspaceRequest = useWorkspaceEventsRequest();

  return useMutation<void, Error, EventRecord["event_id"]>({
    mutationFn: function deleteWorkspaceEvent(eventId: string) {
      return events.deleteEvent(eventId);
    },
    onSuccess: (_, eventId) => {
      queryClient.setQueryData(
        eventQueryKeys.forWorkspace(orgId, workspaceRequest),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }
          return oldData.filter((event) => event.event_id !== eventId);
        },
      );
      // The deleted event may also appear in other list views
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(orgId),
      });
      // Other workspace views may also include this event — mark stale
      // for next mount. `refetchType: "none"` avoids re-fetching the
      // currently-active workspace, which we just updated via
      // `setQueryData` above.
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(orgId),
        refetchType: "none",
      });
    },
  });
}

export function useUpdateWorkspaceEvent() {
  const queryClient = useQueryClient();
  const { events } = useDomainServices();
  const orgId = useCurrentOrgId();

  const workspaceRequest = useWorkspaceEventsRequest();

  return useMutation<
    EventRecord,
    Error,
    { eventId: string; request: UpdateEventRequest }
  >({
    mutationFn: function updateWorkspaceEvent({ eventId, request }) {
      return events.updateEvent(eventId, request);
    },
    onSuccess: (event: EventRecord) => {
      queryClient.setQueryData(
        eventQueryKeys.forWorkspace(orgId, workspaceRequest),
        (oldData: EventRecord[] | undefined) => {
          if (!oldData) {
            return undefined;
          }

          return oldData.map((oldEvent) => {
            if (oldEvent.event_id === event.event_id) {
              return event;
            }
            return oldEvent;
          });
        },
      );

      queryClient.setQueryData(
        eventQueryKeys.detail(orgId, event.event_id),
        event,
      );

      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.lists(orgId),
      });
      // Other workspace views may also include this event — mark stale
      // for next mount. `refetchType: "none"` avoids re-fetching the
      // currently-active workspace, which we just updated via
      // `setQueryData` above.
      void queryClient.invalidateQueries({
        queryKey: eventQueryKeys.forWorkspaces(orgId),
        refetchType: "none",
      });
    },
  });
}

function selectEvents(events: EventRecord[], select: SelectOptions) {
  return events.filter((event) => {
    const {
      datasetIds = [],
      fileIds = [],
      topicIds = [],
      messagePathIds = [],
    } = select;

    if (
      datasetIds.length > 0 &&
      event.associations.some((association) =>
        datasetIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    if (
      fileIds.length > 0 &&
      event.associations.some((association) =>
        fileIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    if (
      topicIds.length > 0 &&
      event.associations.some((association) =>
        topicIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    if (
      messagePathIds.length > 0 &&
      event.associations.some((association) =>
        messagePathIds.includes(association.association_id),
      )
    ) {
      return true;
    }

    return false;
  });
}
